import { getApiUrl } from './subdomain'

const API_BASE = getApiUrl()

interface ApiResponse<T> {
  data?: T
  error?: string
}

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }))
      return { error: error.message || `HTTP ${response.status}` }
    }

    const data = await response.json()
    return { data }
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Network error' }
  }
}

export const api = {
  get: <T>(endpoint: string) => fetchApi<T>(endpoint, { method: 'GET' }),
  post: <T>(endpoint: string, body?: unknown) =>
    fetchApi<T>(endpoint, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(endpoint: string, body?: unknown) =>
    fetchApi<T>(endpoint, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(endpoint: string) => fetchApi<T>(endpoint, { method: 'DELETE' }),
}

// Auth helpers
export const auth = {
  getSession: () => api.get<{ user: User | null }>('/auth/session'),
  logout: () => api.post('/auth/logout'),
  
  // OAuth login redirects
  loginSteam: () => { window.location.href = `${API_BASE}/auth/steam/login` },
  loginEpic: () => { window.location.href = `${API_BASE}/auth/epic/login` },
  loginXbox: () => { window.location.href = `${API_BASE}/auth/xbox/login` },
}

// Stats helpers - use public endpoints for viewing (no auth required)
export const stats = {
  getAll: () => api.get<UserStats>('/public/stats'),
  getSteam: () => api.get<SteamStats>('/public/stats/steam'),
  getFortnite: () => api.get<FortniteStats>('/public/stats/fortnite'),
  getXbox: () => api.get<XboxStats>('/public/stats/xbox'),
  getProfile: () => api.get<{ username: string; linkedAccounts: Record<string, { id: string; username: string }> }>('/public/profile'),
  // Authenticated endpoints for refreshing
  refresh: (platform: string) => api.post(`/stats/${platform}/refresh`),
}

// Types (should match shared package)
export interface User {
  id: string
  username: string
  email?: string
  linkedAccounts: {
    steam?: { id: string; username: string }
    epic?: { id: string; username: string }
    xbox?: { id: string; username: string }
  }
}

export interface UserStats {
  steam?: SteamStats
  fortnite?: FortniteStats
  xbox?: XboxStats
}

export interface SteamStats {
  steamId: string
  personaName: string
  avatarUrl: string
  games: SteamGame[]
  recentlyPlayed: SteamGame[]
}

export interface SteamGame {
  appId: number
  name: string
  playtimeForever: number
  playtimeRecent?: number
  iconUrl?: string
}

export interface FortniteStats {
  accountId: string
  username: string
  battlePass: {
    level: number
    progress: number
  }
  stats: {
    overall: FortniteGameStats
    solo: FortniteGameStats
    duo: FortniteGameStats
    squad: FortniteGameStats
  }
}

export interface FortniteGameStats {
  wins: number
  kills: number
  deaths: number
  kd: number
  matches: number
  winRate: number
  minutesPlayed?: number
  score?: number
  scorePerMatch?: number
  killsPerMatch?: number
  killsPerMin?: number
  playersOutlived?: number
  top3?: number
  top5?: number
  top6?: number
  top10: number
  top12?: number
  top25: number
}

export interface XboxStats {
  gamertag: string
  gamerscore: number
  avatarUrl: string
  recentGames: XboxGame[]
}

export interface XboxGame {
  titleId: string
  name: string
  gamerscore: number
  maxGamerscore: number
  lastPlayed: string
  imageUrl?: string
}
